import React from 'react'
import PropTypes from 'prop-types'
import { useTheme } from '@material-ui/core/styles'
import useMediaQuery from '@material-ui/core/useMediaQuery'
import Box from '@material-ui/core/Box'
import Grid from '@material-ui/core/Grid'
import Typography from '@material-ui/core/Typography'
import TitleNavigation from 'ui/modules/titleNavigation'
import CompanyCard from 'ui/modules/cards/companyCard'
import SimpleList from 'ui/modules/lists/simpleList'
import DocumentCard from 'ui/modules/cards/documentCard'
import ImageCard from 'ui/modules/cards/imageCard'
import BusinessCard from 'ui/modules/cards/businessCard'
import Map from 'ui/modules/map'
import getInitials from '../../../utils/getInitials'
import { ReactComponent as IconProvider } from 'assets/icons/provider.svg'
import { ReactComponent as IconProviderX } from 'assets/icons/provider-x.svg'
import { ReactComponent as IconVerified } from 'assets/icons/verified.svg'
import { ReactComponent as IconDenied } from 'assets/icons/denied.svg'
import ImageHomologated from 'assets/illustrations/supplier-is-homologated.svg'
import ImageNotHomologated from 'assets/illustrations/supplier-is-not-homologated.svg'
import ImageIsProvider from 'assets/illustrations/supplier-is-provider.svg'
import ImageNotProvider from 'assets/illustrations/supplier-is-not-provider.svg'
import useTranslation from 'hooks/useTranslations'

const Vendor = props => {
  const {
    name,
    avatarImage,
    identificationCode,
    productsAndServices,
    documentsRequired,
    documentsOwn,
    certificates,
    businessGroup,
    businessContact,
    capitalStock,
    dateOfIncorporation,
    turnOverAmount,
    turnOverYear,
    overallNumberStaff,
    checkedProvinces,
    onAddVendor,
    goBackMessage,
    homologated,
    isInMyNetwork,
    vendorTypes,
  } = props

  const t = useTranslation()

  documentsRequired.map(item => {
    item.infoText = t('vendor:mandatory-documentation')
    return item
  })

  documentsOwn.map(item => {
    item.infoText = t('vendor:specific-documentation-requirement')
    return item
  })

  const businessContactList = [
    {
      icon: 'user',
      secondary: t('vendor:contact-person'),
      name: businessContact ? businessContact.name : null,
    },
    {
      icon: 'telefone',
      secondary: t('vendor:telephone'),
      name: businessContact ? businessContact.phone : null,
    },
    {
      icon: 'email',
      secondary: t('vendor:email'),
      name: businessContact ? businessContact.email : null,
    },
    {
      icon: 'web',
      secondary: t('vendor:web'),
      name: businessContact ? businessContact.web : null,
    },
    {
      icon: 'location',
      secondary: t('vendor:address'),
      name: businessContact ? businessContact.address : null,
    },
  ].filter(
    element =>
      !(
        ['telefone', 'web', 'location'].includes(element.icon) && !element.name
      ),
  )

  const currencyFormatter = new Intl.NumberFormat(navigator.language, {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  })

  const generalDataList = [
    {
      icon: 'agenda',
      secondary: t('vendor:constitution-date'),
      name: dateOfIncorporation,
    },
    {
      icon: 'coins',
      secondary: t('vendor:social-capital'),
      name: isNaN(parseInt(capitalStock))
        ? undefined
        : currencyFormatter.format(capitalStock),
    },
    {
      icon: 'barChart',
      secondary: t('vendor:sales'),
      name: isNaN(parseInt(turnOverAmount))
        ? undefined
        : currencyFormatter.format(turnOverAmount),
    },
    {
      icon: 'agenda',
      secondary: t('vendor:sales-year'),
      name: turnOverYear,
    },
    {
      icon: 'user',
      secondary: t('vendor:number-of-employees'),
      name: overallNumberStaff,
    },
  ]

  const theme = useTheme()
  const isSmall = useMediaQuery(theme.breakpoints.down('sm'))
  const colsBusiness =
    businessGroup.identification_code &&
    businessGroup.identification_code.length
      ? 4
      : 6
  const colsDocuments = certificates && certificates.length ? 6 : 12

  return (
    <>
      <TitleNavigation page={t(goBackMessage.message, goBackMessage.params)} />

      <Box mb={5}>
        <Grid container spacing={isSmall ? 2 : 4}>
          <Grid item xs={12} sm={12} lg={6}>
            <CompanyCard
              avatarImage={avatarImage}
              avatarName={getInitials(name)}
              company={name}
              nif={identificationCode}
            />
          </Grid>
          {isInMyNetwork ? (
            <Grid item xs={12} sm={6} lg={3}>
              <ImageCard
                isHighlighted={homologated}
                icon={homologated ? IconVerified : IconDenied}
                image={homologated ? ImageHomologated : ImageNotHomologated}
                text={
                  homologated
                    ? t('vendor:homologated')
                    : t('home:not-homologated')
                }
              />
            </Grid>
          ) : null}

          <Grid
            item
            xs={12}
            sm={!isInMyNetwork ? 12 : 6}
            lg={!isInMyNetwork ? 6 : 3}
          >
            <ImageCard
              isHighlighted={isInMyNetwork}
              icon={isInMyNetwork ? IconProvider : IconProviderX}
              image={isInMyNetwork ? ImageIsProvider : ImageNotProvider}
              textButton={!isInMyNetwork ? t('vendor:add') : null}
              text={isInMyNetwork ? t('filter:is-in-my-network') : null}
              onClick={onAddVendor}
            />
          </Grid>
        </Grid>
      </Box>
      {(productsAndServices && productsAndServices.length) ||
      (vendorTypes && vendorTypes.length) ? (
        <Box mb={5}>
          <Grid container spacing={4}>
            {productsAndServices && productsAndServices.length ? (
              <Grid
                item
                xs={12}
                lg={vendorTypes && vendorTypes.length ? 6 : 12}
              >
                <Box>
                  <Box mb={3}>
                    <Typography variant="h1">
                      {t('vendor:products-services')}
                    </Typography>
                  </Box>
                  {productsAndServices === undefined ? (
                    <SimpleList height={364} elements={[]} />
                  ) : (
                    <SimpleList
                      height={364}
                      elements={[...productsAndServices]}
                    />
                  )}
                </Box>
              </Grid>
            ) : null}
            {vendorTypes && vendorTypes.length ? (
              <Grid
                item
                xs={12}
                lg={productsAndServices && productsAndServices.length ? 6 : 12}
              >
                <Box>
                  <Box mb={3}>
                    <Typography variant="h1">{t('vendor:types')}</Typography>
                  </Box>
                  {vendorTypes === undefined ? (
                    <SimpleList height={364} elements={[]} />
                  ) : (
                    <SimpleList height={364} elements={[...vendorTypes]} />
                  )}
                </Box>
              </Grid>
            ) : null}
          </Grid>
        </Box>
      ) : null}

      {(documentsRequired && documentsRequired.length) ||
      (documentsOwn && documentsOwn.length) ? (
        <Box mb={3}>
          <Grid container spacing={4}>
            <Grid item xs={12} lg={colsDocuments}>
              <Box mb={3}>
                <Typography variant="h1">
                  {documentsOwn && documentsOwn.length
                    ? t('vendor:mandatory-and-specific-documents')
                    : t('vendor:mandatory-documents')}
                </Typography>
              </Box>
              {documentsRequired.map((item, index) => (
                <DocumentCard
                  key={`documents-required-${index}`}
                  variant={2}
                  infoText={item.infoText}
                  contentText={item.name}
                  status={item.state}
                />
              ))}
              {documentsOwn.map((item, index) => (
                <DocumentCard
                  key={`documents-own-${index}`}
                  variant={3}
                  infoText={item.infoText}
                  contentText={item.name}
                  status={item.state}
                />
              ))}
            </Grid>
            {certificates && certificates.length ? (
              <Grid item xs={12} lg={6}>
                <Box mb={3}>
                  <Typography variant="h1">
                    {t('vendor:certifications')}
                  </Typography>
                </Box>
                {certificates.map((item, index) => {
                  return (
                    <DocumentCard
                      key={`certificates-${index}`}
                      variant={1}
                      infoText={item.category}
                      contentText={item.name}
                      status={item.state}
                    />
                  )
                })}
              </Grid>
            ) : null}
          </Grid>
        </Box>
      ) : null}
      <Box mb={5}>
        <Grid container spacing={4}>
          {businessGroup.identification_code ? (
            <Grid item xs={12} md={4}>
              <Box>
                <Box mb={3}>
                  <Typography variant="h1">
                    {t('vendor:associated-business-group')}
                  </Typography>
                </Box>
                <BusinessCard
                  nif={businessGroup.identification_code}
                  company={businessGroup.name}
                  country={businessGroup.country_code}
                />
              </Box>
            </Grid>
          ) : null}
          <Grid item xs={12} md={colsBusiness}>
            <Box>
              <Box mb={3}>
                <Typography variant="h1">
                  {t('vendor:commercial-contact')}
                </Typography>
              </Box>
              <SimpleList elements={businessContactList} />
            </Box>
          </Grid>
          <Grid item xs={12} md={colsBusiness}>
            <Box>
              <Box mb={3}>
                <Typography variant="h1">{t('vendor:general-data')}</Typography>
              </Box>
              <SimpleList elements={generalDataList} />
            </Box>
          </Grid>
        </Grid>
      </Box>
      <Box mb={5}>
        <Grid container spacing={4}>
          <Grid item xs={12}>
            <Box>
              <Box mb={3}>
                <Typography variant="h1">
                  {t('vendor:scope-of-action')}
                </Typography>
              </Box>
              <Map checkedProvinces={checkedProvinces} />
            </Box>
          </Grid>
        </Grid>
      </Box>
    </>
  )
}

Vendor.propTypes = {
  name: PropTypes.string,
  avatarImage: PropTypes.string,
  identificationCode: PropTypes.string,
  productsAndServices: PropTypes.array,
  services: PropTypes.array,
  documentsRequired: PropTypes.array,
  documentsOwn: PropTypes.array,
  certificates: PropTypes.array,
  businessGroup: PropTypes.object,
  businessContact: PropTypes.object,
  capitalStock: PropTypes.number,
  dateOfIncorporation: PropTypes.string,
  turnOverAmount: PropTypes.number,
  turnOverYear: PropTypes.number,
  overallNumberStaff: PropTypes.number,
  goBackMessage: PropTypes.object,
  homologated: PropTypes.bool,
  isInMyNetwork: PropTypes.bool,
}

export default Vendor
